import { useEffect, useRef, useState } from 'react';
import { env } from '@/lib/env';

/**
 * Shape returned by `GET /api/public/geo/municipalities`.
 * Mirror of `MunicipalityLookupResult` on the BE.
 */
export interface MunicipalityResult {
  /** ISTAT 6-digit code, e.g. "058091". */
  istatCode: string;
  /** 4-char codice catastale, e.g. "H501" (Roma). */
  cadastralCode: string;
  /** Display name as published by ISTAT, e.g. "Reggio nell'Emilia". */
  name: string;
  /** 2-letter province code, e.g. "RM". */
  provinceCode: string;
  /** 2-digit ISTAT region code (kept for future scoped reports). */
  regionCode: string;
  /** `false` for suppressed (merged / dissolved) comuni. */
  isActive: boolean;
}

/**
 * Search Italian municipalities for the public-wizard MunicipalityPicker.
 *
 * Wraps `GET /api/public/geo/municipalities` (PR-A.1, anonymous, behind
 * publicLimiter 5/min/IP). Debounced 300ms; gated on
 * `q.trim().length >= 2` so we don't burn the rate limit on 1-letter
 * queries.
 *
 * No tanstack-query dependency in this app — vanilla useEffect + fetch
 * is enough for a single anonymous lookup.
 *
 * Latest-wins: each call increments a request id; if a stale response
 * lands after a newer query started, it is dropped. Without this, fast
 * typists can see the previous q's results overwrite the current one.
 */
export function useMunicipalitySearch(
  q: string,
  limit = 10,
): { results: ReadonlyArray<MunicipalityResult>; isLoading: boolean } {
  const [results, setResults] = useState<ReadonlyArray<MunicipalityResult>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    const myReqId = ++reqIdRef.current;
    const handle = window.setTimeout(() => {
      const controller = new AbortController();
      setIsLoading(true);
      fetch(
        `${env.VITE_API_BASE_URL}/api/public/geo/municipalities?q=${encodeURIComponent(trimmed)}&limit=${limit}`,
        { signal: controller.signal },
      )
        .then((response) => {
          if (!response.ok) return null;
          return response.json() as Promise<{
            data?: MunicipalityResult[];
          }>;
        })
        .then((body) => {
          if (myReqId !== reqIdRef.current) return; // stale
          const data = body?.data ?? [];
          setResults(data);
          setIsLoading(false);
        })
        .catch(() => {
          if (myReqId !== reqIdRef.current) return; // stale
          setResults([]);
          setIsLoading(false);
        });
      return () => controller.abort();
    }, 300);

    return () => {
      window.clearTimeout(handle);
    };
  }, [q, limit]);

  return { results, isLoading };
}
